import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Button, Card, Group, Text, Title } from '@grovus/design-system'
import { REQUEST_COPY, type Lang } from '../i18n'
import { fetchCreation, fileCreation, type OwnRequest } from './api'
import { Alert, formatDay, MessageField } from './parts'

interface Props {
  lang: Lang
  /**
   * The boundary's re-entry. Called when the form was rendered for another account, and
   * when the permission turns out to be granted already: either way the screen is no
   * longer the one this session needs.
   */
  onReenter: () => void
  /** The way back, when the caller has one; `clientName` names it. */
  onBack?: () => void
  clientName?: string
}

type View =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'form'; userId: string }
  | { kind: 'pending'; request: OwnRequest }
  | { kind: 'refused' }

type Notice = 'submitFailed' | 'tooLong' | null

/**
 * Screen 5's permission boards for a signed-in user who cannot create clients. A request
 * of either kind still waiting counts as pending: a person admitted by invitation after
 * asking for access keeps that request in the queue, and its approval grants the
 * permission (design, evaluation 18). Opened from No client access and from the client panel.
 */
export function PermissionScreen({ lang, onReenter, onBack, clientName }: Props) {
  const copy = REQUEST_COPY[lang]
  const [view, setView] = useState<View>({ kind: 'loading' })
  const [message, setMessage] = useState('')
  const [notice, setNotice] = useState<Notice>(null)
  const [busy, setBusy] = useState(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  const reenter = useRef(onReenter)
  useEffect(() => { reenter.current = onReenter }, [onReenter])

  const load = useCallback(async () => {
    const result = await fetchCreation()
    // A 401 has already been reported; the boundary is on its way to the sign-in screen.
    if (!mounted.current || result.status === 'stale' || result.status === 'unauthorized') return
    if (result.status !== 'ok') {
      setView({ kind: 'failed' })
      return
    }
    const { userId, canCreateCompanies, request } = result.body
    if (request?.status === 'pending') setView({ kind: 'pending', request })
    else if (canCreateCompanies) reenter.current()
    else if (request?.status === 'rejected') setView({ kind: 'refused' })
    else setView({ kind: 'form', userId })
  }, [])

  useEffect(() => { void load() }, [load])

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (view.kind !== 'form' || busy) return
    setBusy(true)
    setNotice(null)
    const result = await fileCreation(view.userId, message)
    if (!mounted.current || result.status === 'stale') return
    setBusy(false)
    if (result.status === 'ok') {
      setView({ kind: 'pending', request: result.body })
    } else if (result.status === 'changed') {
      // The session is another account's now. The message was written for this one.
      setMessage('')
      onReenter()
    } else if (result.status === 'refused') {
      if (result.code === 'already_approved') onReenter()
      else if (result.request?.status === 'pending') setView({ kind: 'pending', request: result.request })
      else if (result.code === 'already_refused') setView({ kind: 'refused' })
      else setNotice('submitFailed')
    } else if (result.status !== 'unauthorized') {
      setNotice(result.status === 'tooLong' ? 'tooLong' : 'submitFailed')
    }
  }

  const backButton = onBack && (
    <Button variant="subtle" onClick={onBack}>{clientName ? copy.backTo(clientName) : copy.back}</Button>
  )

  return (
    <Card>
      <Title order={1} fz={18}>{copy.permTitle}</Title>

      {view.kind === 'loading' && <Text fz="sm" mt={6} role="status">{copy.loading}</Text>}

      {view.kind === 'failed' && (
        <>
          <Alert>{copy.loadFailed}</Alert>
          <Button mt="md" onClick={() => { setView({ kind: 'loading' }); void load() }}>{copy.retry}</Button>
        </>
      )}

      {view.kind === 'form' && (
        <form onSubmit={onSubmit}>
          <Text fz="sm" mt={6} className="grv-muted">{copy.permLede}</Text>
          <MessageField
            id="permission-message" label={copy.use} copy={copy}
            value={message} onChange={setMessage} disabled={busy}
          />
          {notice !== null && <Alert>{copy[notice]}</Alert>}
          <Group mt="md" gap="sm">
            <Button type="submit" loading={busy} disabled={busy}>{copy.permSubmit}</Button>
            {backButton}
          </Group>
        </form>
      )}

      {view.kind === 'pending' && (
        <>
          <Text fz="sm" mt={6}>{copy.sent(formatDay(view.request.requestedAt, copy))}</Text>
          <Text fz="sm" mt={4} className="grv-muted">{copy.permPendingLede}</Text>
          {backButton && <Group mt="md">{backButton}</Group>}
        </>
      )}

      {view.kind === 'refused' && (
        <>
          <Text fz="sm" mt={6}>{copy.refused}</Text>
          <Text fz="sm" mt={4} className="grv-muted">{copy.unchanged}</Text>
          {backButton && <Group mt="md">{backButton}</Group>}
        </>
      )}
    </Card>
  )
}
